import React from 'react'
import { NavLink, useNavigate, useParams, useLocation } from 'react-router-dom'
import { Text } from '@consta/uikit/Text'
import { IconExit } from '@consta/icons/IconExit'
import { IconFolderOpen } from '@consta/icons/IconFolderOpen'
import { IconWorldStroked } from '@consta/icons/IconWorldStroked'
import { IconWellOpen } from '@consta/icons/IconWellOpen'
import { IconPaste } from '@consta/icons/IconPaste'
import { IconLineAndBarChart } from '@consta/icons/IconLineAndBarChart'
import { IconAreaChart } from '@consta/icons/IconAreaChart'
import { IconInfo } from '@consta/icons/IconInfo'
import { IconDocFilled } from '@consta/icons/IconDocFilled'
import { IconEye } from '@consta/icons/IconEye'
import { IconDrillingRig } from '@consta/icons/IconDrillingRig'
import { IconOperators } from '@consta/icons/IconOperators'
import { IconGrouping } from '@consta/icons/IconGrouping'
//@ts-ignore
import classes from './Sidebar.module.scss'
import { Button } from '@consta/uikit/Button'
import { useState, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { logOut } from '../../store/authSlice'
import { RootState } from '../../store/store'
import { useGetProjectUuidQuery } from '../../services/projectService'
import { useLazyGetByProjectQuery } from '../../services/reportService'

interface SidebarProps {
  isCollapsed: boolean;
  onToggle: () => void;
}

const Sidebar: React.FC<SidebarProps> = ({ isCollapsed, onToggle }) => {
    const [userAccessLevel, setUserAccessLevel] = useState<number | null>(null)
    const [isRegistered, setIsRegistered] = useState<boolean | null>(null)
    const { projectUuid } = useParams<{ projectUuid: string }>()
    const location = useLocation()

    const navigate = useNavigate()
    const dispatch = useDispatch()

    const userInfo = useSelector((state: RootState) => state.auth.userInfo);

    // Добавляем запрос для получения отчета
    const [getByProject] = useLazyGetByProjectQuery();

    const extractProjectUuidFromPath = () => {
        const path = location.pathname;
        const projectMatch = path.match(/\/project\/([a-f0-9-]+)/);
        return projectMatch ? projectMatch[1] : null;
    };

    const currentProjectUuid = projectUuid || extractProjectUuidFromPath();

    const { data: projectData } = useGetProjectUuidQuery(
        { uuid: currentProjectUuid! },
        { skip: !currentProjectUuid }
    );

    // Функция для обработки клика по завершенному мониторингу
    const handleCompletedMonitoringClick = async () => {
        if (!currentProjectUuid) return;
        
        try {
            // Получаем отчет по проекту
            const reportData = await getByProject({
                project_uuid: currentProjectUuid
            }).unwrap();

            if (reportData && reportData.uuid) {
                // Переходим на страницу отчета
                navigate(`/report/${reportData.uuid}`);
            } else {
                // Если отчет не найден, переходим в общий список отчетов
                navigate('/report');
            }
        } catch (error) {
            console.error('Ошибка при получении отчета:', error);
            // В случае ошибки переходим в общий список отчетов
            navigate('/report');
        }
    };

    useEffect(() => {
        if (userInfo) {
            setUserAccessLevel(userInfo.role?.access_level || 0);
            setIsRegistered(userInfo.is_registered || false);
        } else {
            const storedUserInfo = localStorage.getItem('userInfo');
            if (storedUserInfo) {
                try {
                    const parsedUserInfo = JSON.parse(storedUserInfo);
                    setUserAccessLevel(parsedUserInfo.role?.access_level || 0);
                    setIsRegistered(parsedUserInfo.is_registered || false);
                } catch (error) {
                    console.error('Ошибка парсинга userInfo из localStorage:', error);
                    setUserAccessLevel(0);
                    setIsRegistered(false);
                }
            } else {
                setUserAccessLevel(0);
                setIsRegistered(false);
            }
        }
    }, [userInfo]);

    const handleLogout = () => {
        dispatch(logOut());
        //('Пользователь вышел из системы');
        window.location.href = '/platform/';
    };

    const isActivePath = (path: string) => {
        return location.pathname === path || location.pathname.startsWith(path + '/');
    };

    const isInProject = () => {
        return location.pathname.includes('/project/') && currentProjectUuid;
    };

    const shouldShowAdditionalButtons = () => {
        if (userAccessLevel === null || isRegistered === null) return false;
        if (!isRegistered) return false;
        return userAccessLevel >= 1;
    };

    const isProjectsButtonDisabled = () => {
        if (isRegistered === null) return true;
        return !isRegistered;
    };

    const canSeeAdvancedProjectSections = () => {
        return userAccessLevel !== null && userAccessLevel >= 1;
    };

    const ProjectNavItem = ({ 
        to, 
        label, 
        icon: Icon,
        exact = false
    }: {
        to: string;
        label: string;
        icon: any;
        exact?: boolean;
    }) => {
        const getIsActive = () => {
            if (exact) {
                return location.pathname === to;
            } else {
                return location.pathname === to || location.pathname.startsWith(to + '/');
            }
        };

        const isActive = getIsActive();

        return (
            <NavLink 
                to={to} 
                className={`${classes.projectLink} ${isActive ? classes.activeProjectLink : ''}`}
            >
                <div className={classes.projectNavContent}>
                    <Icon size="s" className={classes.projectNavIcon} />
                    {!isCollapsed && (
                        <Text size='s' className={classes.projectNavText}>
                            {label}
                        </Text>
                    )}
                </div>
            </NavLink>
        );
    };

    return (
        <div className={`${classes.container} ${isCollapsed ? classes.collapsed : ''}`}>
            <div className={classes.top}>
                {/* Проекты */}
                {!isInProject() ? (
                    <Button
                        label={!isCollapsed ? 'Проекты' : ''}
                        view='clear'
                        disabled={isProjectsButtonDisabled()}
                        style={isActivePath('/project') ? { color: '#0078d2' } : {}}
                        iconLeft={IconFolderOpen}
                        onClick={() => navigate('/project')}
                        className={classes.navButton}
                    />
                ) : (
                    <div className={classes.projectSection}>
                        <Button
                            label={!isCollapsed ? 'Проекты' : ''}
                            view='clear'
                            style={{ color: '#0078d2' }}
                            iconLeft={IconFolderOpen}
                            onClick={() => navigate('/project')}
                            className={classes.navButton}
                        />
                        {!isCollapsed && (
                            <Text className={classes.projectTitle} size='s'>
                                {projectData?.number ? `Проект №${projectData.number}` : `Проект ${currentProjectUuid}`}
                            </Text>
                        )}
                        <div className={classes.projectNav}>
                            <ProjectNavItem
                                to={`/project/${currentProjectUuid}`}
                                label="Данные о проекте"
                                icon={IconInfo}
                                exact={true}
                            />
                            <ProjectNavItem
                                to={`/project/${currentProjectUuid}/results`}
                                label="Результаты и отчетные материалы"
                                icon={IconDocFilled}
                            />

                            {canSeeAdvancedProjectSections() && (
                                <>
                                    <ProjectNavItem
                                        to={`/project/${currentProjectUuid}/chromatogram`}
                                        label="Хроматограмма"
                                        icon={IconLineAndBarChart}
                                    />
                                    
                                    {/* Мониторинг - всегда активная кнопка */}
                                    <div 
                                        className={`${classes.projectLink} ${location.pathname.includes('/monitoring') ? classes.activeProjectLink : ''}`}
                                        onClick={projectData?.is_completed ? handleCompletedMonitoringClick : undefined}
                                    >
                                        {projectData?.is_completed ? (
                                            // Для завершенного мониторинга - кнопка без NavLink
                                            <div className={classes.projectNavContent} style={{cursor: 'pointer'}}>
                                                {/*@ts-ignore */}
                                                <IconEye size="s" className={classes.projectNavIcon} />
                                                {!isCollapsed && (
                                                    <Text size='s' className={classes.projectNavText}>
                                                        Мониторинг (завершен)
                                                    </Text>
                                                )}
                                            </div>
                                        ) : (
                                            // Для активного мониторинга - обычный NavLink
                                            <NavLink 
                                                //@ts-ignore
                                                to={projectData?.current_stage > 1 ? 
                                                    `/project/${currentProjectUuid}/monitoring/${projectData?.current_stage}` : 
                                                    `/project/${currentProjectUuid}/monitoring`}
                                                className={classes.projectNavContent}
                                            >
                                                {/*@ts-ignore */}
                                                <IconEye size="s" className={classes.projectNavIcon} />
                                                {!isCollapsed && (
                                                    <Text size='s' className={classes.projectNavText}>
                                                        Мониторинг
                                                    </Text>
                                                )}
                                            </NavLink>
                                        )}
                                    </div>

                                    <ProjectNavItem
                                        to={`/project/${currentProjectUuid}/wells`}
                                        label="Скважины"
                                        icon={IconDrillingRig}
                                    />
                                </>
                            )}
                        </div>
                    </div>
                )}

                {shouldShowAdditionalButtons() && (
                    <>
                        <Button
                            label={!isCollapsed ? 'Отчеты' : ''}
                            view='clear'
                            style={isActivePath('/report') ? { color: '#0078d2' } : {}}
                            iconLeft={IconPaste}
                            onClick={() => navigate('/report')}
                            className={classes.navButton}
                        />
                        <Button
                            label={!isCollapsed ? 'Скважины' : ''}
                            view='clear'
                            style={isActivePath('/wells') ? { color: '#0078d2' } : {}}
                            iconLeft={IconWellOpen}
                            onClick={() => navigate('/wells')}
                            className={classes.navButton}
                        />
                        <Button
                            label={!isCollapsed ? 'Месторождения' : ''}
                            view='clear'
                            style={isActivePath('/field') ? { color: '#0078d2' } : {}}
                            iconLeft={IconAreaChart}
                            onClick={() => navigate('/field')}
                            className={classes.navButton}
                        />
                        <Button
                            label={!isCollapsed ? 'Справочник' : ''}
                            view='clear'
                            style={isActivePath('/directory') ? { color: '#0078d2' } : {}}
                            iconLeft={IconGrouping}
                            onClick={() => navigate('/directory')}
                            className={classes.navButton}
                        />
                    </>
                )}
            </div>

            <div className={classes.bottom}>
                {shouldShowAdditionalButtons() && (
                    <Button
                        label={!isCollapsed ? 'Статистика' : ''}
                        view='clear'
                        style={isActivePath('/statistics') ? { color: '#0078d2' } : {}}
                        iconLeft={IconOperators}
                        onClick={() => navigate('/statistics')}
                        className={classes.navButton}
                    />
                )}

                <Button
                    label={!isCollapsed ? 'Перейти на сайт' : ''}
                    view='clear'
                    iconLeft={IconWorldStroked}
                    onClick={() => window.location.href = process.env.REACT_APP_SITE_URL || 'https://geoindicator.ru/'}
                    className={classes.navButton}
                />

                <Button
                    label={!isCollapsed ? 'Выйти' : ''}
                    view='clear'
                    style={{ color: '#ff3b30' }}
                    iconLeft={IconExit}
                    onClick={handleLogout}
                    className={classes.navButton}
                />

                {!isCollapsed && (
                    <Text size='xs' view='secondary' className={classes.copyright}>
                        © {new Date().getFullYear()} ООО «Геоиндикатор»
                    </Text>
                )}
            </div>
        </div>
    )
}

export default Sidebar